<script>
import DOMPurify from 'dompurify';
import moment from 'moment-timezone';
import ENUMS from '@shared/messages/enums.json';
import {
  calendarEventToPayload,
  formatCalendarEventDates,
} from '@shared/utils/calendar';
import AddToCalendarDropdown from '@shared/components/AddToCalendarDropdown.vue';

const LOCATION_ICONS = {
  talkizy: 'video',
  visio: 'video',
  link: 'external-link-alt',
  address: 'map-marker-alt',
};

export default {
  components: { AddToCalendarDropdown },
  messages: {
    frequency: ENUMS.calendarEventFrequency,
    status: ENUMS.calendarEventStatus,
    statusClass: ENUMS.calendarEventStatusClass,
  },
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  computed: {
    localDates() {
      return formatCalendarEventDates(this.event);
    },
    eventTzDates() {
      const tz = this.event.timezone || 'Europe/Paris';

      // Same timezone as the viewer: the local time line already says it all
      if (tz === moment.tz.guess()) return null;

      return formatCalendarEventDates(this.event, tz);
    },
    timezoneLabel() {
      const tz = this.event.timezone || 'Europe/Paris';
      const offset = moment.tz(tz).format('Z');
      return `${tz.replace(/_/g, ' ')} (UTC${offset})`;
    },
    sanitizedDescription() {
      return DOMPurify.sanitize(this.event.description || '');
    },
    locationIcon() {
      return LOCATION_ICONS[this.event.location_type] || 'map-marker-alt';
    },
    // Lien de visioconférence (classe Talkizy ou lien visio saisi) : on affiche un
    // libellé plutôt que l'URL brute (souvent longue, avec token).
    isVisioLink() {
      return ['talkizy', 'visio'].includes(this.event.location_type);
    },
    calendarPayload() {
      return calendarEventToPayload(this.event);
    },
    locationUrl() {
      if (!this.event.location_value || this.event.location_type === 'address') {
        return null;
      }

      try {
        const parsed = new URL(this.event.location_value);
        return ['http:', 'https:'].includes(parsed.protocol) ? this.event.location_value : null;
      } catch (e) {
        return null;
      }
    },
    // Un événement piloté par une classe virtuelle Talkizy n'est pas éditable
    // directement (la classe est la source de vérité).
    isTalkizyMeeting() {
      return !!this.event.talkizy_meeting_id;
    },
    // Espace connecté à Talkizy : sinon on masque « Gérer » (page injoignable).
    isTalkizyConnected() {
      return this.$store.getters['auth/isTalkizyConnected'];
    },
    isTalkizyMember() {
      return this.$store.getters['auth/isTalkizyMember'];
    },
    isPast() {
      if (!this.event.end_date) return false;
      return moment.utc(this.event.end_date).isBefore(moment.utc());
    },
  },
  methods: {
    onEdit() {
      this.$emit('edit', this.event);
      this.$emit('close');
    },
  },
};
</script>

<template>
  <div class="modal-card" style="width: 600px">
    <header class="modal-card-head">
      <p class="modal-card-title">
        Détail de l'événement
      </p>
      <button type="button" class="delete" aria-label="close" @click="$emit('close')" />
    </header>

    <section class="modal-card-body">
      <div class="columns is-multiline">
        <div v-if="event.picture" class="column is-5">
          <figure class="compaedm_cover">
            <img :src="event.picture" :alt="event.title" class="bradius-8">
          </figure>
        </div>

        <div class="column">
          <!-- Status + frequency tags -->
          <p class="is-size-8 mb-2">
            <span class="tag mr-2">
              <b-icon class="mr-1 is-size-9" icon="circle" :type="$options.messages.statusClass[event.status]" />
              {{ $options.messages.status[event.status] }}
            </span>
            <span v-if="event.frequency && event.frequency !== 'once'" class="tag is-info is-light">
              <b-icon icon="redo" size="is-small" class="mr-1" />
              {{ $options.messages.frequency[event.frequency] }}
            </span>
          </p>

          <h2 class="title is-5 mb-3">
            {{ event.title }}
          </h2>

          <div class="is-flex mb-2">
            <b-icon icon="clock" size="is-small" class="compaedm_rowicon mr-2" />
            <p>{{ localDates }}</p>
          </div>

          <div class="is-flex has-text-grey-dark mb-2">
            <b-icon icon="globe" size="is-small" class="compaedm_rowicon mr-2" />
            <div>
              <p class="is-size-8">
                {{ timezoneLabel }}
              </p>
              <p v-if="eventTzDates" class="is-size-8">
                Heure d'origine : {{ eventTzDates }}
              </p>
            </div>
          </div>

          <p v-if="event.location_type" class="is-flex has-text-grey-dark mb-2">
            <b-icon :icon="locationIcon" size="is-small" class="compaedm_rowicon mr-2" />
            <a
              v-if="locationUrl"
              :href="locationUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="compaedm_meta"
            >{{ isVisioLink ? 'Lien vers votre classe virtuelle' : event.location_value }}</a>
            <span v-else class="compaedm_meta">{{ event.location_value }}</span>
          </p>
        </div>
      </div>

      <div v-if="event.description" class="compaedm_about pt-4">
        <p class="has-text-grey-dark has-text-weight-medium is-size-8 is-uppercase mb-2">
          À propos de l'événement
        </p>
        <div class="content" v-html="sanitizedDescription" />
      </div>

      <div v-if="event.trainings && event.trainings.length" class="notification mt-4">
        <p class="has-text-weight-bold is-size-7 mb-2">
          Formations liées
        </p>
        <ul>
          <li
            v-for="training in event.trainings"
            :key="training.uuid"
          >
            {{ training.name }}
          </li>
        </ul>
      </div>
    </section>

    <footer class="modal-card-foot is-justify-content-flex-end">
      <div class="buttons">
        <b-button type="is-text" @click="$emit('close')">
          Fermer
        </b-button>
        <AddToCalendarDropdown
          v-if="!isPast"
          :calendar-event="calendarPayload"
          position="is-top-left"
        >
          <template #trigger>
            <b-tooltip label="Ajouter au calendrier" position="is-left">
              <b-button icon-left="calendar-plus" />
            </b-tooltip>
          </template>
        </AddToCalendarDropdown>
        <b-button
          v-if="isTalkizyMeeting && isTalkizyConnected && isTalkizyMember"
          class="ml-3"
          type="is-primary"
          icon-left="video"
          tag="router-link"
          :to="{ name: 'classe_virtuelle_meeting_settings', params: { meetingId: event.talkizy_meeting_id } }"
          @click.native="$emit('close')"
        >
          Gérer
        </b-button>
        <b-button
          v-if="!isTalkizyMeeting"
          class="ml-3"
          type="is-primary"
          icon-left="pen"
          @click="onEdit"
        >
          Modifier
        </b-button>
      </div>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
.compaedm_cover {
  img {
    display: block;
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    object-position: center;
  }
}

.compaedm_rowicon {
  flex-shrink: 0;
  margin-top: 2px;
}

.compaedm_meta {
  word-break: break-all;
}

.compaedm_about {
  border-top: 1px solid $theme_color_grey_light;
}
</style>
